import React, { useState } from 'react';
import { IconButton } from '@mui/material';
import { OrderAndEye } from 'ui-component/icons';
import { IRecordType } from 'types/recordType';
import { IRecordHeaders } from 'ui-component/records/types';
import { DataFocusViewButtonMenu } from './components';
import { DataFocusViewDialog } from 'ui-component/dialogs';
import { openInANewTabDFVAction, openInThisTabDFVAction } from './utils';
import { useLocalStorageListener, DATA_FOCUS_VIEW_PREF } from 'hooks/useLocalStorageListener';

const MENU_PREF_KEY = 'dataFocusViewMenuPref';

export type DataFocusViewButtonProps = {
    recordId: string;
    recordType: IRecordType;
    onUpdate: (updatedRecord: IRecordHeaders) => void;
};

export const DataFocusViewButton = ({ recordId, recordType, onUpdate }: DataFocusViewButtonProps) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const selectedPref = useLocalStorageListener(MENU_PREF_KEY, 'OPEN_IN_THIS_TAB', Object.values(DATA_FOCUS_VIEW_PREF));
    const [openDialog, setOpenDialog] = useState(false);

    // Handlers for each option
    const handleOpenInThisTab = () => {
        handleMenuClose();
        openInThisTabDFVAction(recordId, recordType);
    };
    const handleOpenInNewTab = () => {
        handleMenuClose();
        openInANewTabDFVAction(recordId, recordType);
    };
    const handleOpenInPopup = () => {
        handleMenuClose();
        setOpenDialog(true);
    };

    // Left click: execute the selected preference
    const handleButtonClick = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        if (event.type === 'click' && event.button === 0) {
            const selected = menuOptions.find((opt) => opt.value === selectedPref);
            if (selected) selected.handler();
        }
    };
    // Right click: open menu
    const handleContextMenu = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        setAnchorEl(event.currentTarget);
    };
    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    // Menu options (inside component, not global const)
    const menuOptions = [
        { label: 'Open in this tab', value: DATA_FOCUS_VIEW_PREF.OPEN_IN_THIS_TAB, handler: handleOpenInThisTab },
        { label: 'Open in a new tab', value: DATA_FOCUS_VIEW_PREF.OPEN_IN_A_NEW_TAB, handler: handleOpenInNewTab },
        { label: 'Open in a pop-up window', value: DATA_FOCUS_VIEW_PREF.OPEN_IN_A_POP_UP_WINDOW, handler: handleOpenInPopup }
    ];

    return (
        <>
            <IconButton size="small" onClick={handleButtonClick} onContextMenu={handleContextMenu} sx={{ ml: 0.5 }}>
                <OrderAndEye fontSize="small" />
            </IconButton>
            <DataFocusViewButtonMenu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                recordId={recordId}
                recordType={recordType}
                onOpenInAPopUp={() => setOpenDialog(true)}
                onDefaultPrefChange={(pref) => {
                    // Dispatch custom event to notify the change
                    window.dispatchEvent(
                        new CustomEvent('localStorageChange', {
                            detail: { key: MENU_PREF_KEY, value: pref }
                        })
                    );
                }}
            />

            {openDialog && (
                <DataFocusViewDialog
                    recordId={+recordId}
                    recordType={recordType}
                    open={openDialog}
                    onClose={(updatedRecord) => {
                        setOpenDialog(false);
                        if (updatedRecord) onUpdate(updatedRecord);
                    }}
                />
            )}
        </>
    );
};
